import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { toValue } from '../components/Crud/useFilter.utils';

interface UseSearchParamsConfig {
  searchParamsToIgnore?: string[];
}

const buildSearchParamsFromValues = (values: any) => {
  const newParams = new URLSearchParams();
  if (!values) {
    return newParams;
  }
  const paramsToAddKeys = Object.keys(values);
  for (let index = 0; index < paramsToAddKeys.length; index += 1) {
    const key = paramsToAddKeys[index];
    const value = values[key];
    if (Array.isArray(value)) {
      value.forEach((val) => newParams.append(key, val));
    } else {
      newParams.append(key, value);
    }
  }
  newParams.sort();
  return newParams;
};

const generateNewUrl = (pathname: string, paramValues: Record<string, any>) => {
  const searchParams = buildSearchParamsFromValues(paramValues);
  return `${pathname}?${searchParams}`;
};

const recoverValuesFromSearchParams = (
  search?: string,
  formatter?: (values: Record<string, any>) => Record<string, any>,
): Record<string, any> => {
  if (!search) {
    return {};
  }

  const searchParams = new URLSearchParams(search);
  const allKeysUnique = Array.from(new Set<string>(searchParams.keys()).values());
  const valuesObjects: { key: string; value: any }[] = allKeysUnique.map((key: string) => {
    const allValuesFromKey = searchParams.getAll(key);
    const verifiedValue = allValuesFromKey.length === 1 ? allValuesFromKey[0] : allValuesFromKey;
    return { key, value: verifiedValue };
  });

  const recoveredValues: Record<string, any> = {};
  valuesObjects.forEach((keyValueObject) => {
    recoveredValues[keyValueObject.key] = toValue(keyValueObject.value);
  });

  return formatter ? formatter(recoveredValues) : recoveredValues;
};

const useSearchParams = (defaultValues?: Record<string, any>, config?: UseSearchParamsConfig) => {
  const history = useHistory();

  const applyValuesToUrl = useCallback(
    (values: any, historyAction: 'push' | 'replace') => {
      const search = `?${buildSearchParamsFromValues(values)}`;
      if (history.location.search === search) {
        return;
      }
      const newUrl = `${generateNewUrl(history.location.pathname, values)}${history.location.hash}`;
      history[historyAction](newUrl, history.location.state);
    },
    [history],
  );

  const updateSearchParams = useCallback(
    (filtersToAdd: Record<string, any>, historyAction: 'push' | 'replace') => {
      const currentFilters = recoverValuesFromSearchParams(history.location.search);
      const allFilters = { ...currentFilters, ...filtersToAdd };
      Object.keys(allFilters).forEach((key) => {
        const currentValue = allFilters[key];
        if (currentValue === undefined) {
          delete allFilters[key];
        }
      });
      applyValuesToUrl(allFilters, historyAction);
    },
    [applyValuesToUrl, history.location.search],
  );

  const setSearchParams = useCallback(
    (filtersToAdd: Record<string, any>) => updateSearchParams(filtersToAdd, 'replace'),
    [updateSearchParams],
  );

  const pushSearchParams = useCallback(
    (filtersToAdd: Record<string, any>) => updateSearchParams(filtersToAdd, 'push'),
    [updateSearchParams],
  );

  const removeSearchParams = useCallback(
    (fields: string[]) => {
      const currentFilters = recoverValuesFromSearchParams(history.location.search);
      const newFilters = { ...currentFilters };

      fields.forEach((field) => delete newFilters[field]);

      applyValuesToUrl(newFilters, 'replace');
    },
    [applyValuesToUrl, history.location.search],
  );

  useEffect(() => {
    if (defaultValues) {
      setSearchParams(defaultValues);
    }
  }, []);

  const lastSearchParams = useRef<Record<string, any>>({});

  const searchParamsFormatter = (values: Record<string, any> = {}) => {
    const valuesWithoutUnnecessaryParams = { ...values };
    config?.searchParamsToIgnore?.forEach((param) => delete valuesWithoutUnnecessaryParams[param]);
    return valuesWithoutUnnecessaryParams;
  };

  const searchParams = useMemo(() => {
    const possibleNewParams = recoverValuesFromSearchParams(
      history.location.search,
      searchParamsFormatter,
    );

    if (!config?.searchParamsToIgnore) {
      return possibleNewParams;
    }

    const lastParams = lastSearchParams.current;
    const isSameParams = _.isEqual(lastParams, possibleNewParams);

    if (isSameParams) {
      return lastParams;
    }

    lastSearchParams.current = possibleNewParams;
    return possibleNewParams;
  }, [history.location.search]);

  return {
    setSearchParams,
    pushSearchParams,
    removeSearchParams,
    searchParams,
  };
};

export default useSearchParams;
